/* eslint-disable react/no-danger */
import React from 'react';

import { ZOLA_DOMAIN } from 'constants/url';
import {
  FAQPage,
  WithContext,
  Question,
  Article,
  BreadcrumbList,
  WebSite,
  ItemList,
} from 'schema-dts';
import { ImageFieldType } from 'libs/contentful/types';

export const renderJsonLdScript = (
  structuredData:
    | WithContext<FAQPage>
    | WithContext<Article>
    | WithContext<BreadcrumbList>
    | ItemList[]
    | WebSite
): JSX.Element => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
  />
);

type Faq = {
  question: string;
  answer: string;
};

export function prependUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  if (url.indexOf('https://') === 0) return url;
  if (url.indexOf('//') === 0) return `https:${url}`;
  return `https://${url}`;
}

const baseStructuredData: { '@context': 'https://schema.org' } = {
  '@context': 'https://schema.org',
};

/**
 * FAQ Documentation
 * @see https://developers.google.com/search/docs/data-types/faqpage
 * @example
 * const faqStructuredData = getFaqStructuredData(FAQ_LD.homepage);
 */
export function getFaqStructuredData(faqArray: Faq[]): WithContext<FAQPage> {
  function mapFaqItem(faqItem: Faq): Question {
    return {
      '@type': 'Question',
      name: faqItem.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faqItem.answer,
      },
    };
  }

  return {
    ...baseStructuredData,
    '@type': 'FAQPage',
    mainEntity: faqArray.map(mapFaqItem),
  };
}

export type Breadcrumb = {
  label?: string;
  to?: string;
};

export function getBreadcrumbStructuredData(
  breadcrumbArray: Breadcrumb[]
): WithContext<BreadcrumbList> {
  return {
    ...baseStructuredData,
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbArray.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item?.label,
      item: `${ZOLA_DOMAIN}${item?.to}`,
    })),
  };
}

export function getArticleStructuredData({
  articleBody,
  fullPathname,
  title,
  heroCaption,
  heroImage,
  publishedAt,
  author,
}: {
  articleBody?: string;
  fullPathname: string;
  title: string;
  heroCaption?: string;
  heroImage?: ImageFieldType;
  publishedAt?: string;
  author?: string;
}): WithContext<Article> {
  const articleImage = heroImage?.src ? [prependUrl(heroImage.src) as string] : undefined;
  return {
    ...baseStructuredData,
    '@type': 'Article',
    articleBody,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': fullPathname,
    },
    headline: title,
    description: heroCaption,
    image: articleImage,
    datePublished: publishedAt,
    author: {
      '@type': 'Person',
      name: author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Zola',
      logo: {
        '@type': 'ImageObject',
        url: 'https://d1tntvpcrzvon2.cloudfront.net/vnassets/static/media/zola-logo.e36c4941.svg',
      },
    },
  };
}
